<template>
  <div
    class="bg-surface shadow-xl relative flex flex-1 flex-col"
    :class="{
      'w-[312px] max-w-full min-h-[464px]': !variant || variant === 'regular',
      'w-full ': variant === 'responsive',
      'rounded-3xl': !channelStore.isAsMonacoFanToken,
    }"
  >
    <div
      v-bind="$attrs"
      class="flex flex-col flex-1 shadow-[inset_6px_7px_14px_0px_rgba(0,0,0,0.25)] gap-1 bg-transparent border-[3px] overflow-hidden border-border-brand"
      :class="[
        {
          '!bg-surface':
            quizCardState.finishedData?.winner ||
            currentState === QuizItemCardState.Question,
          'pt-3': currentState === QuizItemCardState.Question,
          'pt-2':
            currentState === QuizItemCardState.Leaderboard ||
            currentState === QuizItemCardState.Winner,
          'pb-2':
            (currentState === QuizItemCardState.Leaderboard ||
              currentState === QuizItemCardState.Winner) &&
            !showCreatorControls,
          'rounded-3xl': !channelStore.isAsMonacoFanToken,
        },
      ]"
    >
      <!-- Winner card -->
      <ContestFinished
        v-if="currentState === QuizItemCardState.Winner"
        class="flex-1"
        object-name="Quiz"
        :data="quizCardState.finishedData!"
        :allow-switch-to-leaderboard="true"
        @close="toggleWinnerLeaderboardState"
        @delete="onDeleteQuiz"
      >
        <template #actions>
          <IconButtonV2 icon="mdi:ios-share" @click="onShareClick" />
        </template>
      </ContestFinished>

      <!-- Question  -->
      <template v-if="currentState === QuizItemCardState.Question">
        <QuizQuestion
          v-model="selectedOptionId"
          :loading="loading"
          :quiz="quiz"
          :question="quizCardState.currentQuestion"
          :disabled="showCreatorControls"
          :enable-participation-count="
            !!quizCardState.currentQuestion?.selected_option_id ||
            showCreatorControls
          "
          :question-participation-count-map="questionParticipationCountMap"
          :question-participation-total-count="questionParticipationTotalCount"
          @delete="onDeleteQuiz"
          @answer-submitted="onAnswerSubmitted"
        />
        <QuizItemCardQuestionActions
          v-if="!showCreatorControls"
          @answer-submitted="onAnswerSubmitted"
        />
      </template>

      <!-- Leaderboard -->
      <div v-if="currentState === QuizItemCardState.Leaderboard">
        <QuizLeaderboard
          :quiz="quiz as unknown as QuizForLeaderboard"
          :show-close="quizCardState.hasWinner"
          @close="toggleWinnerLeaderboardState"
          @delete="onDeleteQuiz"
          @share="onShareClick"
        />
      </div>

      <template v-if="false">
        <span
          v-if="
            !showCreatorControls &&
            currentState === QuizItemCardState.Leaderboard &&
            (quizCardState.quizStatus === ContestStatus.Ready ||
              !quizCardState.hasWinner)
          "
          class="flex flex-row mx-auto font-normal leading-5 font-serif text-text text-base mt-auto pb-2 mb-3"
        >
          {{ $t("channel.contests.quiz.status.waitingWinner") }}
        </span>
      </template>

      <!-- Winner / Leaderboard Switch -->
      <div
        v-if="showWinnerLeaderBoardSwitch"
        class="flex flex-row mx-auto justify-center items-center p-1 bg-surface-secondary rounded-lg font-serif text-sm text-center text-text mt-auto h-fit mb-2"
      >
        <ContestTooltip side="bottom">
          <template #trigger>
            <button
              class="flex px-4 py-2 rounded-md w-[103px] min-h-8 leading-5 items-center justify-center"
              :class="{
                'shadow-md bg-fill-brand-secondary':
                  currentWinnerLeaderboardState ===
                  WinnerLeaderboardSwitchState.Winner,
              }"
              @click="
                currentWinnerLeaderboardState =
                  WinnerLeaderboardSwitchState.Winner
              "
            >
              <Icon icon="material-symbols:trophy" />
            </button>
          </template>
          <p>
            <span>{{ $t("channel.contests.quiz.winner.title") }}</span>
          </p>
        </ContestTooltip>
        <ContestTooltip side="bottom">
          <template #trigger>
            <button
              class="flex px-4 py-2 rounded-md w-[103px] min-h-8 leading-5 items-center justify-center"
              :class="{
                'shadow-md bg-fill-brand-secondary':
                  currentWinnerLeaderboardState ===
                  WinnerLeaderboardSwitchState.Leaderboard,
              }"
              @click="
                currentWinnerLeaderboardState =
                  WinnerLeaderboardSwitchState.Leaderboard
              "
            >
              <Icon icon="material-symbols:leaderboard" class="" />
            </button>
          </template>
          <p>
            <span>{{ $t("channel.contests.quiz.leaderboard.title") }} </span>
          </p>
        </ContestTooltip>
      </div>

      <!-- Host CTA -->
      <div
        v-if="showCreatorControls"
        class="flex flex-col items-center gap-2 w-full px-3 py-3 bg-surface backdrop-blur-[35px] text-sm mt-auto"
        :class="{ 'rounded-standard': !channelStore.isAsMonacoFanToken }"
      >
        <div class="flex items-start gap-2 self-stretch">
          <ContestButton
            variant="subtle"
            class="!h-8 !px-4 text-xs"
            @click="onEditClick"
          >
            {{ $t("channel.contests.quiz.actions.edit") }}
          </ContestButton>

          <ContestButton
            v-if="props.quiz.status === ContestStatus.Draft"
            variant="primary"
            class="!h-8 !px-4 text-xs"
            :disabled="startQuizLoading"
            :is-loading="startQuizLoading"
            @click="publishConfirmationIsOpen = true"
          >
            {{ $t("channel.contests.quiz.actions.publish") }}
          </ContestButton>

          <ContestTooltip
            side="top"
            :disabled="props.quiz.status !== ContestStatus.Ended"
          >
            <template #trigger>
              <ContestButton
                v-if="
                  props.quiz.status === ContestStatus.Ready ||
                  props.quiz.status === ContestStatus.Ended
                "
                variant="subtle"
                class="!h-8 !px-4 text-xs"
                :disabled="
                  endQuizLoading || props.quiz.status === ContestStatus.Ended
                "
                :is-loading="endQuizLoading"
                @click="showConfirmationModal = true"
              >
                {{ $t("channel.contests.quiz.actions.end") }}
              </ContestButton>
            </template>
            <p>
              <span>{{ $t("channel.contests.quiz.status.ended") }}</span>
            </p>
          </ContestTooltip>
        </div>
      </div>
    </div>

    <!-- User section -->
    <div
      class="h-16 p-4 justify-between items-center inline-flex w-full shadow-2xl"
      :class="{ 'rounded-b-3xl': !channelStore.isAsMonacoFanToken }"
    >
      <div
        class="w-full grow shrink basis-0 h-8 justify-between items-start flex"
      >
        <div class="flex-1 justify-start items-center gap-2 flex">
          <Avatar size="sm" :user="quiz.created_by ?? undefined" />
          <div
            class="text-white text-xs font-medium font-sans leading-[18px] line-clamp-1"
          >
            By {{ quiz.created_by?.username }}
          </div>
        </div>

        <div class="flex flex-row gap-2 shrink-0">
          <ContestTooltip side="bottom">
            <template #trigger>
              <div class="bg-transparent text-xl text-brand-primary size-11">
                <IconButtonV2
                  class="size-11"
                  icon="material-symbols:info-outline"
                />
              </div>
            </template>
            <p>
              <span>{{ tooltipLabel }}</span>
            </p>
          </ContestTooltip>
          <ContestItemDropdown
            v-if="channelStore.permissions.manageContests"
            object-name="Quiz"
            :is-share-enabled="quiz.status !== ContestStatus.Draft"
            @delete="deleteConfirmationIsOpen = true"
            @edit="onEditClick"
            @share="onShareClick"
          />
          <IconButtonV2 v-else icon="mdi:ios-share" @click="onShareClick" />
        </div>
      </div>
    </div>
  </div>

  <DangerConfirmationModal
    v-model="showConfirmationModal"
    :title="$t('channel.contests.quiz.confirmations.end.title')"
    :confirm-label="$t('channel.contests.quiz.confirmations.end.confirm')"
    @confirm="onEndQuiz"
  />

  <DangerConfirmationModal
    v-model="publishConfirmationIsOpen"
    :title="$t('channel.contests.quiz.confirmations.publish.title')"
    :confirm-label="$t('channel.contests.quiz.confirmations.publish.confirm')"
    @confirm="handleStarQuiz"
  />

  <DangerConfirmationModal
    v-model="deleteConfirmationIsOpen"
    :title="$t('channel.contests.quiz.confirmations.delete.title')"
    :confirm-label="$t('channel.contests.quiz.confirmations.delete.confirm')"
    @confirm="onDeleteQuiz"
  />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const QuizItemCardFragment = gql(/* GraphQL */ `
  fragment QuizItemCardData on Quiz {
    id
    __typename
    status
    description
    created_at
    updated_at
    start_time
    end_time
    scheduled_start_time
    scheduled_end_time
    is_visible_in_public_channel_page
    countries_iso
    language
    channel_id
    created_by_id
    send_email_notifications
    notification_sent_at
    ...QuizLeaderboardData
    authorization_rule_group {
      id
      ...AuthorizationRuleGroupForGating
    }
    channel {
      id
      created_at
      updated_at
      is_enabled
      name
      description
      logo
      banner
    }
    quiz_translations {
      language
      description
    }
    prize {
      id
      created_at
      updated_at
      title
      description
      image
      quiz_id
      raffle_id
      contest_prize_translations {
        language
        title
        description
      }
    }
    winner_users {
      id
      created_at
      updated_at
      user_id
      user {
        id
        username
        avatar
        is_verified
        locale
        user_channel_memberships {
          channel_membership {
            permission_mask
          }
        }
      }
      quiz_id
      score_position
    }
    quiz_communities {
      id
      created_at
      updated_at
      quiz_id
      community_id
      community {
        id
        created_at
        updated_at
        name
        image
        description
      }
    }
    quiz_questions {
      id
      created_at
      updated_at
      quiz_id
      order
      title
      status
      is_poll
      selected_option_id
      quiz_question_translations {
        language
        title
      }
      options {
        id
        created_at
        updated_at
        order
        label
        image
        is_correct
        question_id
        user_participation_count
        quiz_question_option_translations {
          language
          label
        }
      }
    }
    created_by {
      id
      username
      avatar
      is_verified
      locale
    }
  }
`);

export type QuizItemCardData = ResultOf<typeof QuizItemCardFragment>;
</script>

<script setup lang="ts">
import {
  ContestStatus,
  DeleteQuizDocument,
  EndQuizDocument,
  StartQuizFromChannelDocument,
} from "@/generated/graphql";
import { computed, ref, defineAsyncComponent } from "vue";
import ContestFinished from "./contest-finished.vue";
import QuizQuestion from "./quizzes/quiz-question.vue";
import QuizLeaderboard, {
  QuizLeaderboardFragment,
} from "./quiz-leaderboard.vue";
import type { ResultOf as ResultOfType } from "@graphql-typed-document-node/core";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { useUserStore } from "@/stores/user.store";
import {
  useProvideGatingPermissions,
  type AuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import { useChannelStore } from "@/stores/channel.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import { useShareReferral } from "@/composables/use-share-referral";
import IconButtonV2 from "@/components/ui/button/icon-button-v2.vue";
import {
  provideQuizCardState,
  type QuizLike,
} from "./quiz-card/quiz-card-state";
import Avatar from "@/components/me/avatar.vue";
import ContestTooltip from "./contest-tooltip.vue";
import ContestItemDropdown from "./contest-item-dropdown.vue";
import ContestButton from "./contest-button.vue";
import QuizItemCardQuestionActions from "./quizzes/quiz-item-card-question-actions.vue";
import { useObjectTranslation } from "@/stores/translation.store";
import { apolloClient } from "@/utils/apollo";

import { Icon } from "@iconify/vue";

type QuizForLeaderboard = ResultOfType<typeof QuizLeaderboardFragment>;

// Lazy load
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const QuizQuestionParticipationCountDocument = gql(/* GraphQL */ `
  query QuizQuestionParticipationCount($questionId: String!) {
    quizQuestionParticipationCount(question_id: $questionId) {
      option_id
      count
    }
  }
`);

const emit = defineEmits<{
  refetch: [];
}>();

const props = defineProps<{
  quiz: QuizItemCardData;
  authorizationRuleGroupFragment: AuthorizationRuleGroupForGatingType;
  variant?: "responsive" | "regular";
  loading: boolean;
}>();

useProvideGatingPermissions(
  computed(() => props.authorizationRuleGroupFragment),
);

const router = useRouter();

const userStore = useUserStore();
const channelStore = useChannelStore();
const trackingService = useTrackingStore();

const quizCardState = provideQuizCardState(
  computed(() => props.quiz as unknown as QuizLike),
);

const selectedOptionId = ref<string | null>(null);

const showConfirmationModal = ref(false);
const publishConfirmationIsOpen = ref(false);
const deleteConfirmationIsOpen = ref(false);

const { text: tooltipLabel } = useObjectTranslation(
  computed(() => {
    const originalText = props.quiz.description;
    if (!originalText) return null;

    return {
      authorUserId: props.quiz.created_by_id ?? props.quiz.created_by?.id,
      originalText: originalText,
      objectAccessors: {
        objectId: props.quiz.id,
        objectProperty: "description",
      },
      translatedText: props.quiz.quiz_translations[0]?.description,
    };
  }),
);

function onEditClick() {
  router.push({
    name: RouteNames.EditQuiz,
    params: { quiz: props.quiz.id, channel: channelStore.channelSlug },
  });
}

const { mutate: startQuiz, loading: startQuizLoading } = useMutation(
  StartQuizFromChannelDocument,
);

async function handleStarQuiz() {
  if (props.quiz.status === ContestStatus.Draft) {
    try {
      await startQuiz({ quiz_id: props.quiz.id });
    } catch (e) {
      console.error(e);
    }
    emit("refetch");
  }
}

const { mutate: endQuiz, loading: endQuizLoading } =
  useMutation(EndQuizDocument);

async function onEndQuiz() {
  try {
    const res = await endQuiz({ quiz_id: props.quiz.id });
    trackingService.track({
      event: TrackEventEnum.ChannelQuizWon,
      category: EventCategory.ChannelQuiz,
      metadata: {
        experienceId: props.quiz?.id,
        experienceType: ExperienceType.Quiz,
        experienceAction: ExperienceAction.WIN_CONTEST,
        experienceCreatorId: props.quiz?.created_by?.id,
        experienceCreatorUsername: props.quiz?.created_by?.username,
        // experienceMembership: channelStore.channelMemberships.find(
        //   (cm) =>
        //     cm.authorization_rule_id === props.quiz?.authorization_rule_id,
        // )?.name,
        experienceCommunityIds: props.quiz?.quiz_communities?.map(
          (c) => c.community.id,
        ),
        experienceCommunityNames: props.quiz?.quiz_communities?.map(
          (c) => c.community.name,
        ),
      },
    });
  } catch (e) {
    console.error(e);
  }
  emit("refetch");
}

const { mutate: deleteQuiz } = useMutation(DeleteQuizDocument);
async function onDeleteQuiz() {
  deleteConfirmationIsOpen.value = false;
  await deleteQuiz({
    quiz_id: props.quiz.id,
  });
  apolloClient.cache.evict({
    id: `${props.quiz.__typename}:${props.quiz.id}`,
  });
  apolloClient.cache.gc();
}

const onShareClick = useShareReferral({
  openGraphObject: computed(() => ({
    title: props.quiz.description,
    image: props.quiz.prize?.image,
  })),
  referralObject: computed(() => ({
    title: props.quiz.description ?? "Earn exclusive rewards in FanFest",
    payload: {
      experience_id: props.quiz.id,
      experience_type: ExperienceType.Quiz,
      community_id:
        props.quiz.quiz_communities?.length === 1
          ? props.quiz.quiz_communities.at(0)?.community.id
          : undefined,
    },
  })),
});

enum QuizItemCardState {
  Question = "Question",
  Leaderboard = "Leaderboard",
  Winner = "Winner",
}

enum WinnerLeaderboardSwitchState {
  Leaderboard = "Leaderboard",
  Winner = "Winner",
}

const currentWinnerLeaderboardState = ref<WinnerLeaderboardSwitchState>(
  WinnerLeaderboardSwitchState.Leaderboard,
);

const toggleWinnerLeaderboardState = () => {
  currentWinnerLeaderboardState.value =
    currentWinnerLeaderboardState.value ===
    WinnerLeaderboardSwitchState.Leaderboard
      ? WinnerLeaderboardSwitchState.Winner
      : WinnerLeaderboardSwitchState.Leaderboard;
};

const fanCanAnswer = computed(() => {
  return (
    quizCardState.currentQuestion &&
    props.quiz.status === ContestStatus.Ready &&
    quizCardState.hasOpenQuestions
  );
});

const lastVotedQuestion = computed(() => {
  if (!quizCardState.lastVotedQuestionId) return null;
  return props.quiz.quiz_questions?.find(
    (q) => q.id === quizCardState.lastVotedQuestionId,
  );
});

const {
  result: questionParticipationCountResult,
  refetch: refetchParticipationCount,
} = useQuery(
  QuizQuestionParticipationCountDocument,
  computed(() => ({
    questionId: quizCardState.currentQuestion!.id,
  })),
  {
    enabled: computed(() => !!quizCardState.currentQuestion),
    keepPreviousResult: false,
  },
);

const questionParticipationCountMap = computed(() => {
  const map = {} as Record<string, number>;

  if (questionParticipationCountResult.value) {
    const userParticipationList =
      questionParticipationCountResult.value.quizQuestionParticipationCount;

    for (const { option_id, count } of userParticipationList) {
      map[option_id] = (map?.[option_id] ?? 0) + count;
    }
  }

  return map;
});

const questionParticipationTotalCount = computed(() => {
  return Object.values(questionParticipationCountMap.value).reduce(
    (acc, count) => acc + count,
    0,
  );
});

const showCreatorControls = computed(() => {
  if (!userStore.user) return false;

  const isOwner = props.quiz.created_by_id === userStore.userId;
  const isChannelAdmin = channelStore.permissions.manageChannel;

  return (
    (props.quiz.status === ContestStatus.Draft ||
      props.quiz.status === ContestStatus.Ready) &&
    (isOwner || isChannelAdmin)
  );
});

const currentState = computed(() => {
  // Question
  if (
    fanCanAnswer.value ||
    showCreatorControls.value ||
    lastVotedQuestion.value?.is_poll
  ) {
    return QuizItemCardState.Question;
  }

  // Winner
  if (
    quizCardState.quizStatus === ContestStatus.Ended &&
    quizCardState.hasWinner &&
    quizCardState.finishedData &&
    currentWinnerLeaderboardState.value === WinnerLeaderboardSwitchState.Winner
  ) {
    return QuizItemCardState.Winner;
  }

  // Leaderboard
  return QuizItemCardState.Leaderboard;
});

const showWinnerLeaderBoardSwitch = computed(() => {
  return (
    [QuizItemCardState.Winner, QuizItemCardState.Leaderboard].includes(
      currentState.value,
    ) &&
    quizCardState.quizStatus === ContestStatus.Ended &&
    quizCardState.hasWinner
  );
});

function onAnswerSubmitted() {
  emit("refetch");
  refetchParticipationCount();
}
</script>
